import { useCallback, useEffect } from "react";
import { create } from "zustand";
import {
  AccountingMode,
  canUseAccountantMode,
  defaultModeFor,
  type Role,
} from "@bruhat/shared";
import { usePermissions } from "@/hooks/use-permissions";

/**
 * Which accounting interface this person is looking at.
 *
 * The mode decides vocabulary and routing — nothing else. It grants no access:
 * every endpoint is permission-gated independently, so switching to Accountant
 * Mode shows an accountant's *words*, never an accountant's data unless the
 * person could already see it.
 *
 * The choice is remembered per user in this browser. A site manager who has
 * never chosen lands in Simple Mode; an accountant lands in the existing
 * screens, exactly as before this layer existed.
 *
 * ---
 *
 * It lives in a shared store rather than in each component's own state, and
 * that is the whole point of this file.
 *
 * It used to be `useState` inside the hook, which gives every caller its own
 * private copy. Five components read the mode — the top bar, the sidebar, the
 * landing redirect, the not-found page and the home-path helper — so toggling
 * it in the top bar changed the top bar and told none of the others. The
 * sidebar went on calling the accountant's dashboard "Business overview", and
 * links pointed at the mode you were no longer in, until a full page reload
 * made everyone agree again. That is what made it intermittent: reload and it
 * is fine, toggle and it is not.
 */

const STORAGE_PREFIX = "bruhat.accounting-mode";

function storageKey(userId: string | undefined): string {
  // Per user: two people sharing a machine should not inherit each other's
  // choice, and it is a preference rather than anything sensitive.
  return `${STORAGE_PREFIX}.${userId ?? "anon"}`;
}

function read(userId: string | undefined): AccountingMode | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw === AccountingMode.SIMPLE || raw === AccountingMode.ACCOUNTANT
      ? raw
      : null;
  } catch {
    // Private windows and blocked site data throw here. A preference is not
    // worth failing a render over.
    return null;
  }
}

interface ModeStore {
  /** Null until it has been read for the signed-in user. */
  mode: AccountingMode | null;
  /** Who the stored mode belongs to, so a different sign-in re-reads it. */
  userId: string | undefined;
  adopt: (userId: string | undefined, mode: AccountingMode | null) => void;
}

const useModeStore = create<ModeStore>((set) => ({
  mode: null,
  userId: undefined,
  adopt: (userId, mode) => set({ userId, mode }),
}));

export interface AccountingModeState {
  mode: AccountingMode;
  isSimple: boolean;
  /** False for roles whose work never needs debits and credits. */
  canSwitch: boolean;
  setMode: (next: AccountingMode) => void;
  toggle: () => void;
}

export function useAccountingMode(): AccountingModeState {
  const { roles, userId } = usePermissions();
  const typedRoles = roles as Role[];

  const allowed = canUseAccountantMode(typedRoles);
  const fallback = defaultModeFor(typedRoles);

  const stored = useModeStore((s) => s.mode);
  const storedFor = useModeStore((s) => s.userId);
  const adopt = useModeStore((s) => s.adopt);

  /*
    Roles arrive after the profile loads, so the default can change under us on
    first paint, and a different user signing in must not inherit the previous
    one's choice. Re-read whenever the user changes; a stored choice always wins
    over a recomputed default.
  */
  useEffect(() => {
    if (storedFor === userId && stored !== null) return;
    adopt(userId, read(userId) ?? fallback);
  }, [userId, storedFor, stored, fallback, adopt]);

  const current = storedFor === userId ? stored : null;

  const setMode = useCallback(
    (next: AccountingMode) => {
      // Someone who may not open Accountant Mode cannot be put there, whatever
      // is in storage or in a stale link.
      const safe =
        next === AccountingMode.ACCOUNTANT && !allowed
          ? AccountingMode.SIMPLE
          : next;
      adopt(userId, safe);
      try {
        localStorage.setItem(storageKey(userId), safe);
      } catch {
        // Preference not remembered; the session still works.
      }
    },
    [allowed, userId, adopt],
  );

  const effective =
    (current ?? fallback) === AccountingMode.ACCOUNTANT && !allowed
      ? AccountingMode.SIMPLE
      : (current ?? fallback);

  return {
    mode: effective,
    isSimple: effective === AccountingMode.SIMPLE,
    canSwitch: allowed,
    setMode,
    toggle: () =>
      setMode(
        effective === AccountingMode.SIMPLE
          ? AccountingMode.ACCOUNTANT
          : AccountingMode.SIMPLE,
      ),
  };
}
